// Comments pinned to elements: the pure parts, used by the server (check an
// anchor, name the element a thread is on) and by the browser (find what a click
// landed on, place a pin).
//
// An anchor remembers where a comment was put twice. u and v are the spot inside
// the element's box, 0 to 1, so the pin follows the element when somebody drags
// or resizes it. x and y are the scene point that was clicked, for when the
// element is gone.

export const MAX_COMMENTS = 500;

const LINEAR = new Set(["arrow", "line"]);
const clamp01 = (n) => Math.min(1, Math.max(0, n));

/** Live (not deleted) elements by id. */
export const liveById = (elements) => new Map(elements.filter((el) => !el.isDeleted).map((el) => [el.id, el]));

/** The box an element covers before it is turned, in scene units. Lines and arrows by their points. */
export const elementBox = (el) => {
  if (Array.isArray(el.points) && el.points.length) {
    const xs = el.points.map(([px]) => el.x + px);
    const ys = el.points.map(([, py]) => el.y + py);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
  }
  return { x: el.x, y: el.y, w: el.width ?? 0, h: el.height ?? 0 };
};

/**
 * A scene point turned by `angle` radians around the middle of the element's
 * box, the way Excalidraw draws a rotated element. Turning by -el.angle takes a
 * point on screen into the element's own unrotated frame; +el.angle takes it back.
 */
export const turnAround = (el, x, y, angle) => {
  if (!angle) return { x, y };
  const box = elementBox(el);
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: cx + (x - cx) * cos - (y - cy) * sin, y: cy + (x - cx) * sin + (y - cy) * cos };
};

/** The scene point at u, v inside an element, where it is on screen once the element is turned. */
const pointAt = (el, u, v) => {
  const box = elementBox(el);
  return turnAround(el, box.x + u * box.w, box.y + v * box.h, el.angle);
};

/** Where a scene point sits inside an element's box, 0 to 1 each way (the middle for a flat box). */
export const positionIn = (el, sceneX, sceneY) => {
  const { x, y } = turnAround(el, sceneX, sceneY, -(el.angle ?? 0));
  const box = elementBox(el);
  const within = (n, origin, size) => (size > 0 ? clamp01((n - origin) / size) : 0.5);
  return { u: within(x, box.x, box.w), v: within(y, box.y, box.h) };
};

const MAX_LABEL = 120;

/** A label bound to a shape or an arrow stands for that shape or arrow. */
const containerOf = (byId, el) => (el.type === "text" && el.containerId ? byId.get(el.containerId) ?? null : null);

/** What an element says: its bound label, its own text, or a section's name. Null when it says nothing. */
export const elementLabel = (byId, el) => {
  let text = "";
  if (el.type === "text") text = el.text;
  else if (el.type === "frame") text = el.name;
  else {
    const bound = (el.boundElements ?? []).find((b) => b.type === "text");
    text = bound ? byId.get(bound.id)?.text : "";
  }
  return text?.trim() ? text : null;
};

/**
 * Turn what a client sent into the anchor that is stored.
 *
 * With an element, the element must be on the board, and a bound label stands
 * for its container. Missing numbers are filled in: the click point from the
 * box, or the position in the box from the click point, and the box's top-right
 * corner when neither was sent. Without an element, x and y are all there is.
 *
 * @returns {{ok: true, anchor: object} | {ok: false, error: string}}
 */
export const resolveAnchor = (elements, anchor) => {
  if (anchor.elementId === undefined) return { ok: true, anchor: { x: anchor.x, y: anchor.y } };

  const byId = liveById(elements);
  const asked = byId.get(anchor.elementId);
  if (!asked) return { ok: false, error: `anchor.elementId: no element with id "${anchor.elementId}" on this board` };

  const el = containerOf(byId, asked) ?? asked;
  const inBox = el === asked && anchor.u !== undefined;
  const hasPoint = anchor.x !== undefined;

  // With no point given (an agent naming a box), the pin goes on the top-right
  // corner, where it does not cover the box's label.
  const { u, v } = inBox ? anchor : hasPoint ? positionIn(el, anchor.x, anchor.y) : { u: 1, v: 0 };
  const { x, y } = hasPoint ? anchor : pointAt(el, u, v);
  return { ok: true, anchor: { elementId: el.id, u, v, x, y } };
};

/** Where a pin goes: on its element if it is still there, else where it was clicked. */
export const pinPoint = (byId, anchor) => {
  const el = anchor.elementId ? byId.get(anchor.elementId) : null;
  if (!el) return { x: anchor.x, y: anchor.y, gone: Boolean(anchor.elementId) };
  return { ...pointAt(el, anchor.u, anchor.v), gone: false };
};

/**
 * The element a thread is on, said in words: its label, and whether it has been
 * deleted. A long label is cut to 120 characters, since every thread repeats it.
 */
export const describeAnchor = (byId, anchor) => {
  const el = anchor.elementId ? byId.get(anchor.elementId) : null;
  const label = el ? elementLabel(byId, el) : null;
  const on = label && label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
  return { on, gone: Boolean(anchor.elementId) && !el };
};

const distanceToSegment = (px, py, [ax, ay], [bx, by]) => {
  const dx = bx - ax;
  const dy = by - ay;
  const t = dx || dy ? clamp01(((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

/** Whether a point is on an element: inside its box, or within `slop` of a line or arrow. */
const covers = (el, sceneX, sceneY, slop) => {
  const { x, y } = turnAround(el, sceneX, sceneY, -(el.angle ?? 0));
  if (LINEAR.has(el.type) && el.points?.length > 1) {
    const path = el.points.map(([px, py]) => [el.x + px, el.y + py]);
    return path.slice(1).some((to, i) => distanceToSegment(x, y, path[i], to) <= slop);
  }
  const box = elementBox(el);
  return x >= box.x - slop && x <= box.x + box.w + slop && y >= box.y - slop && y <= box.y + box.h + slop;
};

/**
 * The element a click at a scene point lands on: the topmost one drawn there,
 * a bound label standing for its container. A section is only returned when
 * nothing inside it was hit, so a box in a section is commentable.
 */
export const hitTest = (elements, { x, y }, slop = 0) => {
  const live = elements.filter((el) => !el.isDeleted);
  const byId = new Map(live.map((el) => [el.id, el]));
  let section = null;
  for (let i = live.length - 1; i >= 0; i -= 1) {
    const el = live[i];
    if (!covers(el, x, y, slop)) continue;
    if (el.type === "frame") section ??= el;
    else return containerOf(byId, el) ?? el;
  }
  return section;
};

const inIdOrder = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Comments by id, oldest first. Of two copies of one comment the one with the
 * higher version wins, the incoming one on a tie, so a slow list request that
 * lands after a newer socket frame cannot undo it. The version counts writes;
 * clocks are not used, because two servers' clocks can disagree.
 */
export const withComments = (list, incoming) => {
  const merged = new Map(list.map((c) => [c.id, c]));
  for (const c of incoming) {
    const had = merged.get(c.id);
    if (!had || (c.version ?? 0) >= (had.version ?? 0)) merged.set(c.id, c);
  }
  return [...merged.values()].sort(inIdOrder);
};

/** Comments grouped into threads: each first comment with its replies, both in time order. */
export const threadsOf = (comments) => {
  const sorted = [...comments].sort(inIdOrder);
  const replies = new Map();
  for (const c of sorted) if (c.parentId) replies.set(c.parentId, [...(replies.get(c.parentId) ?? []), c]);
  return sorted.filter((c) => !c.parentId).map((root) => ({ root, replies: replies.get(root.id) ?? [] }));
};

/** The open threads, small enough to sit in a board summary an agent reads. */
export const compactThreads = (comments, elements) => {
  const live = liveById(elements);
  return threadsOf(comments)
    .filter(({ root }) => !root.resolved)
    .map(({ root, replies }) => ({
      id: root.id,
      elementId: root.anchor?.elementId ?? null,
      on: describeAnchor(live, root.anchor ?? {}).on,
      body: root.body,
      author: root.author.name,
      replies: replies.map((r) => ({ id: r.id, body: r.body, author: r.author.name })),
    }));
};
